import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { EmptyState } from "@/components/states";
import { useAuth } from "@/lib/auth";
import { SERVICE_LABEL, type ServiceType } from "@/lib/services";
import {
  VISIT_STATUSES,
  VISIT_STATUS_CLASS,
  VISIT_STATUS_LABEL,
  cancelVisitRequest,
  effectiveSlot,
  formatVisitSlot,
  listCustomerVisitRequests,
  type VisitStatus,
} from "@/lib/visits";

export const Route = createFileRoute("/_authenticated/customer/visit-requests")({
  head: () => ({
    meta: [
      { title: "My Visit Requests · Landoo" },
      {
        name: "description",
        content:
          "Track your Landoo property visit requests and their status: pending, accepted, rescheduled, completed or cancelled.",
      },
      { property: "og:title", content: "My Visit Requests · Landoo" },
      { property: "og:description", content: "Track your Landoo property visit requests." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CustomerVisitRequestsPage,
});

type Tab = VisitStatus | "all";
const TABS: Tab[] = ["all", ...VISIT_STATUSES];

function CustomerVisitRequestsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["customer", "visit-requests", user?.id],
    queryFn: () => listCustomerVisitRequests(user!.id),
    enabled: !!user,
  });

  const cancelM = useMutation({
    mutationFn: (id: string) => cancelVisitRequest(id, user!.id),
    onSuccess: () => {
      toast.success("Visit request cancelled.");
      qc.invalidateQueries({ queryKey: ["customer", "visit-requests", user?.id] });
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not cancel the request."),
  });

  const visible = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.status === tab)),
    [rows, tab],
  );

  return (
    <DashboardShell title="Visit Requests" role="Customer" items={CUSTOMER_NAV}>
      <div className="space-y-4">
        <header className="rounded-3xl border border-border bg-card p-4 shadow-soft">
          <h1 className="text-lg font-bold text-brown-dark">My property visit requests</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Every partner update (accepted, rescheduled, completed or cancelled) reaches you as a
            notification.
          </p>
        </header>

        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                tab === t
                  ? "bg-brown-dark text-primary-foreground"
                  : "border border-border bg-card text-brown-dark hover:bg-secondary"
              }`}
            >
              {t === "all" ? "All" : VISIT_STATUS_LABEL[t]}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="rounded-3xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading your visit requests…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState
            title="No visit requests"
            description="Open a listing and tap “Request Visit” to schedule a property visit."
          />
        ) : (
          <ul className="space-y-3">
            {visible.map((v) => {
              const slot = effectiveSlot(v);
              const cancellable =
                v.status === "pending" || v.status === "accepted" || v.status === "rescheduled";
              return (
                <li key={v.id} className="rounded-3xl border border-border bg-card p-4 shadow-soft">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link
                        to="/property/$id"
                        params={{ id: v.listing_id }}
                        className="truncate text-sm font-bold text-brown-dark hover:underline"
                      >
                        {v.listing_title ?? "Listing"}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {SERVICE_LABEL[v.service_type as ServiceType] ?? v.service_type}
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${VISIT_STATUS_CLASS[v.status]}`}
                    >
                      {VISIT_STATUS_LABEL[v.status]}
                    </span>
                  </div>

                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <div className="rounded-2xl border border-border px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {v.status === "rescheduled" ? "Partner's new slot" : "Your slot"}
                      </div>
                      <div className="text-sm font-medium text-brown-dark">
                        {formatVisitSlot(slot.date, slot.time)}
                      </div>
                    </div>
                    {v.status === "rescheduled" && (
                      <div className="rounded-2xl border border-border px-3 py-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Originally requested
                        </div>
                        <div className="text-sm font-medium text-brown-dark">
                          {formatVisitSlot(v.preferred_date, v.preferred_time)}
                        </div>
                      </div>
                    )}
                  </div>

                  {v.message && (
                    <p className="mt-3 rounded-2xl bg-secondary px-3 py-2 text-xs text-brown-dark">
                      {v.message}
                    </p>
                  )}
                  {v.partner_note && (
                    <p className="mt-2 text-xs text-muted-foreground">Partner: {v.partner_note}</p>
                  )}

                  {cancellable && (
                    <button
                      type="button"
                      disabled={cancelM.isPending}
                      onClick={() => cancelM.mutate(v.id)}
                      className="mt-3 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary disabled:opacity-60"
                    >
                      Cancel request
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DashboardShell>
  );
}
